import yup from "../../../../../../utils/yup-extended";
import {KycSocialMediaOptions} from "../form-fields/social-links";
import {parseFromBackend} from "../form-fields/date-of-birth";
import moment from "moment";
import {isPossible, isValid} from "../../../../../../shared/phone";

export const createKycBasicFormStepFormValidationSchema = yup.object().shape({
  legalName: yup
    .string()
    .trim()
    .required("Legal name is required")
    .typeError("Must be a valid string"),
  businessName: yup.string().trim().typeError("Must be a valid string"),
  businessDescription: yup
    .string()
    .trim()
    .required("Business description is required")
    .typeError("Must be a valid string"),
  socialLinks: yup
    .array()
    .of(
      yup.object().shape({
        name: yup.string().trim().required("Name is required"),
        link: yup.string().trim().urlWithEmoji().required("Link is required"), // <- nice and tidy
      })
    )
    .min(1, "Add at least one")
    .test("unique-platforms", "Platforms must be unique", (links = []) => {
      const platforms = links.map((link) => link.name);
      return new Set(platforms).size === platforms.length;
    }),
  // emailAddress: yup
  //   .string()
  //   .trim()
  //   .email("Enter a valid email address")
  //   .required("Email is required")
  //   .typeError("Must be a valid string"),
  dateOfBirth: yup
    .string()
    .required("Date of birth is required")
    .test({
      name: "parse",
      message: "Date of birth is required",
      test(value) {
        return !!parseFromBackend(value);
      },
    })
    .test({
      name: "older-than-18",
      message: "You must be at least 18 years old",
      test(value) {
        const m = parseFromBackend(value);
        if (!m) return false; // already invalid → fail

        // true if ≤ today-18y, false otherwise
        return m.isSameOrBefore(moment().subtract(18, "years").endOf("day"), "day");
      },
    }),
  gender: yup
    .string()
    .oneOf(["Male", "Female", "Other"], "Select a gender")
    .required("Gender is required"),
  country: yup
    .string()
    .trim()
    .required("Country is required")
    .typeError("Must be a valid string"),
  address: yup
    .string()
    .trim()
    .required("Residential address is required")
    .typeError("Must be a valid string"),
  postalCode: yup
    .string()
    .trim()
    .required("Postal code is required")
    .typeError("Must be a valid string"),
  numberCode: yup
    .string()
    .trim()
    .required("Country code is required")
    .typeError("Must be a valid string"),
  phoneNumber: yup
    .string()
    .trim()
    .required("Phone number is required")
    .test({
      name: "phone-possible",
      message: "Number length doesn't match the selected country",
      test(value) {
        if (!value) return true;
        const {numberCode} = this.parent as {numberCode?: string};
        return isPossible(numberCode ?? "", value);
      },
    })
    .test({
      name: "phone-valid",
      message: "Enter a valid phone number for the selected country",
      test(value) {
        if (!value) return true;
        const {numberCode} = this.parent as {numberCode?: string};
        return isValid(numberCode ?? "", value);
      },
    })
    .typeError("Must be a valid string"),
});

export type CreateKycBasicFormStepFormValuesYup = yup.InferType<
  typeof createKycBasicFormStepFormValidationSchema
>;
export type CreateKycBasicFormStepFormValues = {
  legalName: CreateKycBasicFormStepFormValuesYup["legalName"];
  businessName: CreateKycBasicFormStepFormValuesYup["businessName"];
  businessDescription: CreateKycBasicFormStepFormValuesYup["businessDescription"];
  socialLinks: {
    name: KycSocialMediaOptions;
    link: string;
  }[];
  emailAddress: string;
  dateOfBirth: CreateKycBasicFormStepFormValuesYup["dateOfBirth"];
  gender: CreateKycBasicFormStepFormValuesYup["gender"];
  country: CreateKycBasicFormStepFormValuesYup["country"];
  address: CreateKycBasicFormStepFormValuesYup["address"];
  postalCode: CreateKycBasicFormStepFormValuesYup["postalCode"];
  numberCode: CreateKycBasicFormStepFormValuesYup["numberCode"];
  phoneNumber: CreateKycBasicFormStepFormValuesYup["phoneNumber"];
};
